type SeatCountSource =
  | {
      id?: string | null;
      uuid?: string | null;
      usersCount?: number | null;
      users?: readonly unknown[] | null;
    }
  | null
  | undefined;

/**
 * Stripe seat quantity for a team subscription (TA-10847).
 *
 * Each source is tried in order. Within a source, `usersCount` (the
 * AccountUser counter cache the backend bills seats from) wins over
 * `users.length`, because most team reads no longer select the users list.
 * Falls back to `fallback` when no source has either field, and never
 * returns less than 1.
 */
export const resolveTeamSeatCount = (
  sources: readonly SeatCountSource[],
  fallback = 1
): number => {
  for (const source of sources) {
    const count = source?.usersCount ?? source?.users?.length;
    if (count != null) return Math.max(count, 1);
  }
  return Math.max(fallback, 1);
};

/**
 * Seat count for the paywall trial-confirmation flow in MainPanel.web
 * (TA-10847 QA). The modal shows this number and the trial subscription is
 * created with it, so what the user confirms is what Stripe bills.
 *
 * `paywallTeam` is a snapshot set when the paywall opened (MembersTab passes
 * its full team, users array included), so both its `users.length` and its
 * `usersCount` freeze at that moment. `team` is MainPanel's live,
 * cache-watched reader for the open team. When both are the same team the
 * live one is read first, so a membership change while the flow is open
 * still reaches Stripe; the snapshot only fills in when the live read has no
 * count. A different (or missing) open team keeps QA's `paywallTeam ?? team`
 * order (PR #6068 review). Within a source `usersCount` beats
 * `users.length`. Before any team exists the owner plus the invitations
 * collected in the paywall invite step count.
 */
export const resolvePaywallSeatCount = ({
  paywallTeam,
  team,
  pendingMemberCount,
}: {
  paywallTeam: SeatCountSource;
  team: SeatCountSource;
  pendingMemberCount: number;
}): number => {
  const sources = isSameTeam(paywallTeam, team)
    ? [team, paywallTeam]
    : [paywallTeam ?? team];
  return resolveTeamSeatCount(sources, pendingMemberCount + 1);
};

const isSameTeam = (a: SeatCountSource, b: SeatCountSource): boolean =>
  (!!a?.uuid && a.uuid === b?.uuid) || (!!a?.id && a.id === b?.id);
